import {
  AfterViewInit,
  Component,
  computed,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {
  ZiroFormConfig,
  ZiroFormsUpdate,
  ZiroFormsValidationState,
} from 'projects/components/src/lib/forms/ziro-forms-models';
import { Observable, of, Subscription } from 'rxjs';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { ZiroRadioConfig } from '../../../../forms/fields/radio/ziro-radio.component';
import { TranslateModule } from '@ngx-translate/core';
import { AutoAttendantAudioMetadata, Prompt } from '../../../../shared/models/generated/ziroModels';
import { ZiroFileUploadConfig } from '../../../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroTextareaConfig } from '../../../../forms/fields/textarea/ziro-textarea.component';
import { isEqual } from 'lodash';
import { AudioFileResource } from '../../../../shared/resources/audio-file.resource';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';

export enum CallFlowGreetingsOptions {
  NONE = 'NONE',
  CUSTOM_GREETING = 'CUSTOM_GREETING',
  TTS_MESSAGE = 'TTS_MESSAGE',
}

export interface CallFlowGreetingsFormData {
  greetings: CallFlowGreetingsOptions;
  greetingTtsMessage: string | null;
  greetingUpload: AutoAttendantAudioMetadata | File | null;
}

export interface CallFlowGreetingsEntity {
  greeting: Prompt | null;
  greetingFile?: File | null;
  selectedGreetingOption?: CallFlowGreetingsOptions;
  isDestinationsGreeting?: boolean;
}

@Component({
  selector: 'ziro-call-flow-greetings',
  imports: [ZiroFormsModule, TranslateModule],
  templateUrl: './call-flow-greetings-form.component.html',
  styleUrl: './call-flow-greetings-form.component.scss',
})
export class CallFlowGreetingsFormComponent
  extends ZiroFormAbstractDirective<CallFlowGreetingsEntity, CallFlowGreetingsFormData>
  implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
  // inputs
  dataAutomation = input.required<string>();
  prompt = input<Prompt>();
  isDestinationsGreeting = input<boolean>(false);
  isDisabled = input<boolean>(false);
  tenantId = input.required<string>();
  pendingGreetingOption = input<CallFlowGreetingsOptions>();
  pendingGreetingFile = input<File>();
  // outputs
  greetingUpdate = output<CallFlowGreetingsEntity>();
  validityChange = output<boolean>();

  // audio preview signals
  greetingUploadURL = signal('');
  greetingDownloadName = signal('');
  greetingFileExtension = signal('');
  isGreetingLoading = signal(false);

  private _audioFileResource = inject(AudioFileResource);
  private _initialGreetingName: string | null = null;

  radioOptions = computed(() => {
    const isDestinationsGreeting = this.isDestinationsGreeting();
    if (isDestinationsGreeting) {
      return [
        {
          value: CallFlowGreetingsOptions.CUSTOM_GREETING,
          label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.audio.option',
        },
        {
          value: CallFlowGreetingsOptions.TTS_MESSAGE,
          label:
            'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.greeting_message.option',
        },
      ];
    }
    return [
      {
        value: CallFlowGreetingsOptions.NONE,
        label:
          'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.no_greeting.option',
      },
      {
        value: CallFlowGreetingsOptions.CUSTOM_GREETING,
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.audio.option',
      },
      {
        value: CallFlowGreetingsOptions.TTS_MESSAGE,
        label:
          'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.greeting_message.option',
      },
    ];
  });

  acceptedFileTypes = ['.wav', '.wma', '.mp3'];
  formConfig: ZiroFormConfig;

  private _greetingUpdateSubscribed = false;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.formConfig = {
      fields: {
        greetings: {
          label: () =>
            this.isDestinationsGreeting()
              ? 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.menu_option_greeting.label'
              : 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.call_flow_greeting.label',
          dataAutomation: 'call-flow-greeting-type',
          required: () => true,
          disabled: () => this.isDisabled(),
          disabledTooltip: 'tkey;admin.teams_voice_management.teams_auto_attendants.coming_soon.tooltip',
          componentConfig: new ZiroRadioConfig<CallFlowGreetingsOptions>({
            stackLabelAndOptions: true,
            buttons: this.radioOptions(),
          }),
        },
        greetingUpload: {
          dataAutomation: 'call-flow-greeting-upload',
          label:
            'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.greeting_audio.upload.label',
          required: () => this.formData.greetings === CallFlowGreetingsOptions.CUSTOM_GREETING,
          disabled: () => this.isDisabled(),
          disabledTooltip: 'tkey;admin.teams_voice_management.teams_auto_attendants.coming_soon.tooltip',
          componentConfig: new ZiroFileUploadConfig({
            acceptedFileExtensions: { acceptedExtensions: this.acceptedFileTypes.join(', '), allowOthers: false },
            isAudioFile: true,
          }),
        },
        greetingTtsMessage: {
          dataAutomation: 'call-flow-greeting-tts',
          label:
            'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.greetings.greeting.greeting_message.textarea.label',
          required: () => this.formData.greetings === CallFlowGreetingsOptions.TTS_MESSAGE,
          hidden: () => this.formData.greetings !== CallFlowGreetingsOptions.TTS_MESSAGE,
          disabled: () => this.isDisabled(),
          disabledTooltip: 'tkey;admin.teams_voice_management.teams_auto_attendants.coming_soon.tooltip',
          componentConfig: new ZiroTextareaConfig(),
        },
      },
      options: {
        columnClasses: {
          label: 'text-start',
          input: '',
        },
      },
    } as ZiroFormConfig;

    const pendingFile = this.pendingGreetingFile();
    const newEntity: CallFlowGreetingsEntity = {
      greeting: this.prompt() ?? null,
      greetingFile: pendingFile,
      selectedGreetingOption: this.pendingGreetingOption(),
    };
    this.entitySource.next(newEntity);

    if (pendingFile) {
      this._setGreetingPreview(pendingFile);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prompt'] && !isEqual(changes['prompt'].previousValue, changes['prompt'].currentValue)) {
      const newPrompt = this.prompt();
      const newEntity: CallFlowGreetingsEntity = {
        greeting: newPrompt ?? null,
        greetingFile: this.entity ? this.entity.greetingFile : this.pendingGreetingFile(),
        selectedGreetingOption: newPrompt
          ? this.pendingGreetingOption()
          : (this.pendingGreetingOption() ?? CallFlowGreetingsOptions.NONE),
      };

      if (!isEqual(newEntity, this.entity)) {
        this.entitySource.next(newEntity);

        if (newPrompt?.audio?.id && !this._initialGreetingName) {
          this._initialGreetingName = newPrompt.audio.name ?? null;
          this.isGreetingLoading.set(true);
          this._audioFileResource.getAudioFile(this.tenantId(), 'auto-attendant', newPrompt.audio.id).subscribe({
            next: (file) => {
              this._setGreetingPreview(file);
              this.isGreetingLoading.set(false);
            },
            error: () => this.isGreetingLoading.set(false),
          });
        }
      }
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    if (!this._greetingUpdateSubscribed) {
      this._greetingUpdateSubscribed = true;
      const formSub = this.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<CallFlowGreetingsEntity>) => {
        if (update.new && update.old && !isEqual(update.new, update.old)) {
          this._handleAudioUpdate(update.new);
          this.greetingUpdate.emit({ ...update.new, isDestinationsGreeting: this.isDestinationsGreeting() });
        }
        if (update.valid !== ZiroFormsValidationState.PENDING) {
          this.validityChange.emit(update.valid === ZiroFormsValidationState.VALID);
        }
      });
      this._subscriptions.add(formSub);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  toFormData = (entity: CallFlowGreetingsEntity): CallFlowGreetingsFormData => {
    const resolvedGreetingOption: CallFlowGreetingsOptions =
      entity.selectedGreetingOption !== undefined && entity.selectedGreetingOption !== null
        ? entity.selectedGreetingOption
        : entity.greeting?.audio
          ? CallFlowGreetingsOptions.CUSTOM_GREETING
          : entity.greeting?.ttsMessage
            ? CallFlowGreetingsOptions.TTS_MESSAGE
            : CallFlowGreetingsOptions.NONE;

    const greetings =
      this.isDestinationsGreeting() && resolvedGreetingOption === CallFlowGreetingsOptions.NONE
        ? null
        : resolvedGreetingOption;
    return {
      greetings,
      greetingTtsMessage: entity.greeting?.ttsMessage ?? null,
      greetingUpload: entity.greetingFile ?? entity.greeting?.audio ?? null,
    };
  };

  toEntity = (formData: CallFlowGreetingsFormData): CallFlowGreetingsEntity => {
    const previousGreeting = this.entity?.greeting ?? null;

    if (!formData.greetings) {
      return {
        greeting: previousGreeting,
        greetingFile: null,
        selectedGreetingOption: undefined,
      };
    }

    if (formData.greetings === CallFlowGreetingsOptions.NONE) {
      return {
        greeting: previousGreeting,
        greetingFile: null,
        selectedGreetingOption: CallFlowGreetingsOptions.NONE,
      };
    }

    const isCustomGreeting = formData.greetings === CallFlowGreetingsOptions.CUSTOM_GREETING;
    const isTtsMessage = formData.greetings === CallFlowGreetingsOptions.TTS_MESSAGE;

    const selectedAudio =
      formData.greetingUpload instanceof File
        ? null
        : ((formData.greetingUpload as AutoAttendantAudioMetadata | null) ?? previousGreeting?.audio ?? null);

    return {
      selectedGreetingOption: formData.greetings,
      greeting: {
        audio: isCustomGreeting ? selectedAudio : (previousGreeting?.audio ?? null),
        ttsMessage: isTtsMessage ? formData.greetingTtsMessage : (previousGreeting?.ttsMessage ?? null),
      },
      greetingFile: isCustomGreeting && formData.greetingUpload instanceof File ? formData.greetingUpload : null,
    };
  };

  triggerSubmitValidation(): void {
    this._setIsFormSubmitted(true);
    this.validateAllFields();
  }

  protected submit(): Observable<any> {
    return of(null);
  }

  private _handleAudioUpdate(entity: CallFlowGreetingsEntity): void {
    const file = entity.greetingFile;

    if (file instanceof File) {
      this._setGreetingPreview(file);
    } else if (entity.greeting?.audio === null && entity.greeting !== null) {
      this.greetingUploadURL.set('');
      this.greetingDownloadName.set('');
      this.greetingFileExtension.set('');
    }
  }

  private _setGreetingPreview(file: File): void {
    const lastDot = file.name.lastIndexOf('.');
    this.greetingFileExtension.set(file.name.substring(lastDot));
    this.greetingUploadURL.set(URL.createObjectURL(file));
    this.greetingDownloadName.set(file.name);
  }

  protected readonly CallFlowGreetingsOptions = CallFlowGreetingsOptions;
}
